import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { getToken, onTokenChange, refreshSession } from './api';

/**
 * Who is signed in, read from the access token. Only shapes the UI; the APIs
 * check the token themselves.
 */
export interface SiteSession {
  accountId: string;
  login: string;
  /** Signed in with a second factor (TOTP, passkey or recovery code). */
  mfa: boolean;
  /** Has back-office permissions (token `grants`): shows «Gestão» in the switcher. */
  staff: boolean;
}

export function decodeSession(token: string): SiteSession | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const json = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))) as {
      sub?: unknown;
      login?: unknown;
      mfa?: unknown;
      grants?: unknown;
    };
    if (typeof json.sub !== 'string') return null;
    return {
      accountId: json.sub,
      login: typeof json.login === 'string' ? json.login : '',
      mfa: json.mfa === true,
      staff: Array.isArray(json.grants) && json.grants.length > 0,
    };
  } catch {
    return null;
  }
}

interface SessionState {
  session: SiteSession | null;
  /** True until the refresh cookie from an earlier visit has been tried. */
  restoring: boolean;
}

const SessionContext = createContext<SessionState>({ session: null, restoring: true });

export function SessionProvider({ children }: { children: ReactNode }) {
  const [token, setTokenState] = useState<string | null>(getToken);
  const [restoring, setRestoring] = useState(true);

  useEffect(() => onTokenChange(setTokenState), []);
  useEffect(() => {
    void refreshSession().finally(() => setRestoring(false));
  }, []);

  const session = token ? decodeSession(token) : null;
  return (
    <SessionContext.Provider value={{ session, restoring: restoring && !session }}>
      {children}
    </SessionContext.Provider>
  );
}

export function useSession(): SessionState {
  return useContext(SessionContext);
}

/**
 * Only accepts a path on this site (`/painel`), never `//other.site` or a
 * full URL, so a crafted link cannot send someone elsewhere after signing in.
 */
export function safeReturnPath(value: string | null, fallback = '/'): string {
  if (value === null || !value.startsWith('/') || value.startsWith('//')) return fallback;
  if (value.includes('\\')) return fallback;
  return value;
}

/** `/entrar?voltar=<where the person was going>`. */
export function signInPath(returnTo: string): string {
  return `/entrar?voltar=${encodeURIComponent(returnTo)}`;
}

/** Pages behind sign-in send people to it and bring them back afterwards. */
export function RequireSession({ children }: { children: ReactNode }) {
  const { session, restoring } = useSession();
  const location = useLocation();
  if (restoring) return null;
  if (!session) {
    return <Navigate to={signInPath(location.pathname + location.search)} replace />;
  }
  return <>{children}</>;
}
